'use es6';

import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import EmailAddressPattern from 'PatternValidationJS/patterns/EmailAddress';
import GraderActionCreator from '../actions/GraderActionCreator';
import HomepageLabelledInput from '../components/HomepageLabelledInput';
import UrlPattern from 'PatternValidationJS/patterns/Url';
import InputUtil from '../util/InputUtil';
import { navigateTo } from '../util/History';
import { TESTS } from '../constants/Paths';
import { getEmail } from '../selectors/Contact';
import { getSearchParams } from '../util/Urls';
import useQuery from '../hooks/useQuery';
var PRIVACY_POLICY_URL = 'https://legal.hubspot.com/privacy-policy';

var getLangPolicyUrl = function getLangPolicyUrl() {
  var lang = window.I18N_MANUAL_LANG;

  if (lang !== 'en') {
    switch (lang) {
      case 'pt':
        return "https://legal.hubspot.com/br/privacy-policy";

      case 'ja':
        return "https://legal.hubspot.com/jp/privacy-policy";

      default:
        return "https://legal.hubspot.com/" + lang + "/privacy-policy";
    }
  }

  return PRIVACY_POLICY_URL;
};

var HomepageForm = function HomepageForm() {
  var _useQuery = useQuery(),
      _useQuery$website = _useQuery.website,
      website = _useQuery$website === void 0 ? '' : _useQuery$website;

  var dispatch = useDispatch();

  var _useState = useState(website),
      _useState2 = _slicedToArray(_useState, 2),
      websiteUrl = _useState2[0],
      setWebsiteUrl = _useState2[1];

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      ignoreValidity = _useState4[0],
      setIgnoreValidity = _useState4[1];

  var contactEmail = useSelector(getEmail);

  var _useState5 = useState(contactEmail),
      _useState6 = _slicedToArray(_useState5, 2),
      emailAddress = _useState6[0],
      setEmailAddress = _useState6[1];

  var isWebsiteValid = function isWebsiteValid() {
    return UrlPattern.test(websiteUrl);
  };

  var isEmailValid = function isEmailValid() {
    return EmailAddressPattern.test(emailAddress);
  };

  var isInputValid = function isInputValid() {
    return isEmailValid() && isWebsiteValid();
  };

  var formSubmitted = function formSubmitted() {
    dispatch(GraderActionCreator.storeContact(emailAddress));
  };

  var getButtonKey = function getButtonKey() {
    return 'home.form.button';
  };

  var buttonClicked = function buttonClicked(event) {
    // Don't ignore validity anymore
    setIgnoreValidity(false);

    if (isInputValid()) {
      var parsedWebsiteUrl = InputUtil.parseWebsiteUrl(websiteUrl);
      var search = getSearchParams(); // Go to the results page

      navigateTo(TESTS + "/" + parsedWebsiteUrl + search); // Form has been submitted and we've moving to the next page

      formSubmitted();
    }

    event.preventDefault();
    return false;
  };

  var handleSubmit = function handleSubmit(event) {
    event.preventDefault();
    return false;
  };

  return /*#__PURE__*/_jsxs("form", {
    className: "website-input",
    "data-test-id": "homepage-form",
    onSubmit: handleSubmit,
    children: [/*#__PURE__*/_jsx(HomepageLabelledInput, {
      type: "url",
      "data-test-id": "url",
      errorText: I18n.text('home.form.website.errorText'),
      labelText: I18n.text('home.form.website.text'),
      onChange: function onChange(event) {
        var value = event.target.value;
        setWebsiteUrl(value);
      },
      onBlur: function onBlur(event) {
        var value = event.target.value;
        setWebsiteUrl(value.trim());
      },
      value: websiteUrl,
      isValid: function isValid() {
        return ignoreValidity || isWebsiteValid();
      }
    }), /*#__PURE__*/_jsx(HomepageLabelledInput, {
      type: "email",
      "data-test-id": "email",
      errorText: I18n.text('home.form.email.errorText'),
      noticeText: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "home.form.email.noticeText",
        options: {
          target: getLangPolicyUrl()
        }
      }),
      labelText: I18n.text('home.form.email.text'),
      onChange: function onChange(event) {
        var value = event.target.value;
        setEmailAddress(value);
      },
      onBlur: function onBlur(event) {
        var value = event.target.value;
        setEmailAddress(value.trim());
      },
      value: emailAddress,
      isValid: function isValid() {
        return ignoreValidity || isEmailValid();
      }
    }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx("button", {
      "data-test-id": "homepage-button-submit",
      className: "button primary",
      type: "submit",
      onClick: buttonClicked,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: getButtonKey()
      })
    })]
  });
};

export default HomepageForm;